/**
 * FASE 21.3 — PAYLOAD VALIDATION LAYER
 *
 * Validates, sanitizes, and normalizes mutation payloads
 * before they reach any adapter.
 *
 * Returns { ok, payload, errors, warnings }.
 * Caller must check ok before proceeding.
 */

import {
  getMutationSchemaById,
  ALLOWED_VISIT_STATUSES,
  ALLOWED_VISIT_TYPES,
  ALLOWED_PRAYER_TYPES,
  ALLOWED_PERSON_STATUS_FIELDS,
} from "./mutationSchemas";

const MAX_STRING_LENGTHS = {
  contact_name:  100,
  title:         150,
  content:       2000,
  description:   500,
  location_name: 200,
  notes:         1000,
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}(:\d{2})?$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* ─── Type guards ──────────────────────────────────────── */

function isNonEmptyString(v) { return typeof v === "string" && v.trim().length > 0; }
function isValidDate(v)      { return typeof v === "string" && DATE_RE.test(v); }
function isValidUUID(v)      { return typeof v === "string" && UUID_RE.test(v); }

/* ─── Per-tool validators ──────────────────────────────── */

const EXTRA_VALIDATORS = {
  create_visit: (p, errors) => {
    if (p.visit_type && !ALLOWED_VISIT_TYPES.includes(p.visit_type)) {
      errors.push(`visit_type inválido: "${p.visit_type}". Usa: ${ALLOWED_VISIT_TYPES.join(", ")}`);
    }
    if (p.time && !TIME_RE.test(p.time)) errors.push("time debe ser HH:MM");
    if (p.target_family_id && p.target_person_id) {
      errors.push("No se puede tener target_family_id y target_person_id al mismo tiempo");
    }
  },
  schedule_followup: (p, errors) => {
    if (p.followup_date && !isValidDate(p.followup_date)) errors.push("followup_date debe ser YYYY-MM-DD");
    if (p.visit_type && !ALLOWED_VISIT_TYPES.includes(p.visit_type)) {
      errors.push(`visit_type inválido: "${p.visit_type}"`);
    }
  },
  create_prayer: (p, errors) => {
    if (p.prayer_type && !ALLOWED_PRAYER_TYPES.includes(p.prayer_type)) {
      errors.push(`prayer_type inválido: "${p.prayer_type}". Usa: ${ALLOWED_PRAYER_TYPES.join(", ")}`);
    }
  },
  create_event: (p, errors) => {
    if (p.time && !TIME_RE.test(p.time)) errors.push("time debe ser HH:MM");
  },
  update_visit_status: (p, errors) => {
    if (p.new_status && !ALLOWED_VISIT_STATUSES.includes(p.new_status)) {
      errors.push(`new_status inválido: "${p.new_status}". Usa: ${ALLOWED_VISIT_STATUSES.join(", ")}`);
    }
    if (p.visit_id && !isValidUUID(p.visit_id)) errors.push("visit_id debe ser un UUID válido");
  },
  update_person_status: (p, errors) => {
    if (p.status_field && !ALLOWED_PERSON_STATUS_FIELDS.includes(p.status_field)) {
      errors.push(`status_field inválido: "${p.status_field}". Usa: ${ALLOWED_PERSON_STATUS_FIELDS.join(", ")}`);
    }
    if (p.person_id && !isValidUUID(p.person_id)) errors.push("person_id debe ser un UUID válido");
  },
};

/* ─── Sanitizer ────────────────────────────────────────── */

function sanitizePayload(raw, schema) {
  const allowed = new Set([...schema.requiredFields, ...schema.optionalFields]);
  const clean   = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!allowed.has(key)) continue; // drop unknown fields
    if (typeof value === "string") {
      const trimmed = value.trim().replace(/\s+/g, " ");
      if (MAX_STRING_LENGTHS[key] && trimmed.length > MAX_STRING_LENGTHS[key]) {
        clean[key] = trimmed.slice(0, MAX_STRING_LENGTHS[key]);
      } else {
        clean[key] = trimmed;
      }
    } else {
      clean[key] = value;
    }
  }
  return clean;
}

/* ─── Public API ────────────────────────────────────────── */

/**
 * @param {string} toolId
 * @param {Object} rawPayload
 * @returns {{ ok: boolean, payload: Object, errors: string[], warnings: string[] }}
 */
export function validateMutationPayload(toolId, rawPayload) {
  const schema = getMutationSchemaById(toolId);
  if (!schema) {
    return { ok: false, payload: {}, errors: [`Tool desconocido: ${toolId}`], warnings: [] };
  }

  const errors   = [];
  const warnings = [];

  // Strip unknown fields first
  const payload = sanitizePayload(rawPayload ?? {}, schema);

  // Required fields check
  for (const field of schema.requiredFields) {
    const val = payload[field];
    if (val == null || val === "") {
      errors.push(`Campo requerido faltante: ${field}`);
    } else if (typeof val === "string" && !isNonEmptyString(val)) {
      errors.push(`Campo requerido vacío: ${field}`);
    }
  }

  // Date fields
  if (payload.date && !isValidDate(payload.date)) errors.push("date debe ser YYYY-MM-DD");
  if (payload.followup_date && !isValidDate(payload.followup_date)) errors.push("followup_date debe ser YYYY-MM-DD");

  // UUID checks for ID fields
  const uuidFields = ["target_family_id", "target_person_id", "entity_id", "church_id", "district_id"];
  for (const f of uuidFields) {
    if (payload[f] != null && !isValidUUID(payload[f])) {
      errors.push(`${f} debe ser un UUID válido`);
    }
  }

  // Per-tool extra validators
  const extraValidator = EXTRA_VALIDATORS[toolId];
  if (extraValidator) extraValidator(payload, errors);

  return { ok: errors.length === 0, payload, errors, warnings };
}

/**
 * Returns only the missing required fields from a partial payload.
 * Used by field recovery flow (FASE 21.7).
 */
export function getMissingFields(toolId, partialPayload) {
  const schema = getMutationSchemaById(toolId);
  if (!schema) return [];
  return schema.requiredFields.filter((f) => {
    const v = partialPayload?.[f];
    return v == null || v === "";
  });
}
